import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import { getFormatDateWithDaysAgo } from 'utilities/date';
import { CollapsibleContent, CollapsibleDisclosure, useCollapsibleState } from 'components/Collapsible';
import { updateInaccurateAnswer, updateUpVoteAnswer } from 'features/productQuestionsAndAnswers/slice';
import { utagLinkPDP } from 'features/tealium';

import './ProductAnswerCard.scss';

import { getDisplayUserName } from '../utility';

import ProductSubmitReplyForm from './ProductSubmitReplyForm';
import ProductAnswerCardInaccurateFlag from './ProductAnswerCardInaccurateFlag';
import ProductAnswerUpvote from './ProductAnswerUpvote';
import { answerPropTypes } from './propTypes';
import ProductQuestionsAndAnswersThankYou from './ProductQuestionsAndAnswersThankYou';
import ProductAnswerReplyCard from './ProductAnswerReplyCard';

const ProductAnswerCard = ({
	answer, questionPos, pageNumber, fromSearch = false
}) => {

	const {
		id, text, user, dateCreated, replies, upvoted, flagged, recommendedProducts
	} = answer;

	const dispatch = useDispatch();
	const [ inaccurate, setInaccurate ] = useState(Boolean(flagged));
	const [ showThankYouMessage, setShowThankYouMessage ] = useState(false);
	const collapsibleShowHideReply = useCollapsibleState(false);

	const handleInaccurateClick = () => {

		dispatch(updateInaccurateAnswer(id));
		setInaccurate(true);

	};

	const handleReplySubmit = () => {

		collapsibleShowHideReply.setOpen(false);
		setShowThankYouMessage(true);

	};

	const handleCancelClick = () => {

		collapsibleShowHideReply.setOpen(false);

	};

	return (
		<div className="ProductAnswerCard" data-question-num={questionPos} data-page-num={pageNumber}>
			<ProductAnswerUpvote
				upvoted={Boolean(upvoted)}
				handleUpvote={() => {

					dispatch(updateUpVoteAnswer(id));
					if (!upvoted && !fromSearch && pageNumber && questionPos) {

						utagLinkPDP('TurnTo')({
							eventAction: 'Q&A-Love-Answer',
							eventLabel: `${pageNumber}-${questionPos}`
						});

					}

				}}
			/>
			<div className="ProductAnswerCard__body">
				<div className="ProductAnswerCard__text">
					<span dangerouslySetInnerHTML={{ __html: text }} />
				</div>
				<div className="ProductAnswerCard__recommendedProducts">
					{Array.isArray(recommendedProducts)
						&& recommendedProducts.map((recommendedProduct) => (
							<div
								className="ProductAnswerCard__recommendedProductsImage"
								key={recommendedProduct.shortSku}
							>
								<a
									href={recommendedProduct.url}
									className="ProductAnswerCard__recommendedProductsImageLink"
								>
									<img
										src={recommendedProduct.imageUrl}
										className="ProductAnswerCard__recommendedProductsImageUrl"
										alt=""
										height="96"
										width="96"
									/>
									<span className="ProductAnswerCard__recommendedProductsImageTitle">
										{recommendedProduct.title}
									</span>
								</a>
							</div>
						))}
				</div>
				<div className="ProductAnswerCard__userContainer">
					<div className="ProductAnswerCard__btnContainer">
						<CollapsibleDisclosure
							{...collapsibleShowHideReply}
							onClick={(e, isOpen) => {

								if (!fromSearch && pageNumber && questionPos && isOpen) {

									utagLinkPDP('TurnTo')({
										eventAction: 'Q&A-replyLink',
										eventLabel: `${pageNumber}-${questionPos}`
									});

								}

							}}
						>
							Reply
						</CollapsibleDisclosure>
						<ProductAnswerCardInaccurateFlag
							handleInaccurateClick={handleInaccurateClick}
							inaccurate={inaccurate}
						/>
					</div>
					<div className="ProductAnswerCard__username">
						{getDisplayUserName(user)}
						{user?.badge && (
							<span className="ProductAnswerCard__badge">
								{user.badge.text}
							</span>
						)}
					</div>
					<div className="ProductAnswerCard__dateCreated">
						{getFormatDateWithDaysAgo(dateCreated)}
					</div>
				</div>
				<CollapsibleContent {...collapsibleShowHideReply}>
					<ProductSubmitReplyForm
						answerId={id}
						replyTo={getDisplayUserName(user)}
						onSubmit={handleReplySubmit}
						onCancel={handleCancelClick}
						pageNumber={pageNumber}
						questionPos={questionPos}
						fromSearch={fromSearch}
					/>
				</CollapsibleContent>

				{showThankYouMessage && (
					<div className="ProductAnswerCard__replyThankYouMessage">
						<ProductQuestionsAndAnswersThankYou message="Your reply has been submitted. If you provided your email, you may receive notifications for 14 days. Otherwise, return to this page to see any replies." />
					</div>
				)}
				{replies.length > 0 && (
					<div className="ProductAnswerCard__replyList">
						{replies.map((reply) => (
							<ProductAnswerReplyCard
								answerId={id}
								key={reply.id}
								reply={reply}
								pageNumber={pageNumber}
								questionPos={questionPos}
							/>
						))}
					</div>
				)}
			</div>
		</div>
	);

};

ProductAnswerCard.propTypes = {
	answer: answerPropTypes,
	pageNumber: PropTypes.number,
	questionPos: PropTypes.number,
	fromSearch: PropTypes.bool
};

export default ProductAnswerCard;
